import { useState } from "react";
import { Zap, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const GATEWAY_LABEL = { viva: "Viva Wallet", revolut: "Revolut" };

// Only benefits that are actually implemented in the backend.
const PERKS = [
  "Cupón de bienvenida de 7 €",
  "Envío gratis en tus pedidos",
  "El doble de puntos de fidelidad",
  "Acceso a las ofertas VIP del restaurante",
];

export function PortalPrimeBanner({ restaurantId, phone, customerName = "", gateways = [], planPrice = 3.99 }) {
  const [gateway, setGateway] = useState(gateways[0] || "");
  const [loading, setLoading] = useState(false);

  const canPay = gateways.length > 0 && Boolean(phone);

  const startCheckout = async () => {
    if (!canPay || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/prime/checkout`, {
        restaurant_id: restaurantId,
        phone,
        customer_name: customerName,
        gateway: gateway || gateways[0],
        return_url: `${window.location.origin}/portal/${restaurantId}`,
      });
      const url = res.data?.checkout_url;
      if (!url) throw new Error("missing_checkout_url");
      // Remember the intent so the portal can confirm the payment on return.
      try {
        localStorage.setItem(`prime_intent_${restaurantId}`, res.data.intent_id);
      } catch { /* private mode — the webhook still activates Prime */ }
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50 p-5 shadow-sm" data-testid="portal-prime-banner">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-purple-600 flex items-center justify-center shrink-0">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <span className="font-black text-purple-900 text-base">Kaiso Prime</span>
        <span className="ml-auto text-xs font-bold text-purple-500">
          {Number(planPrice).toFixed(2).replace(".", ",")} €
        </span>
      </div>

      <p className="text-sm text-purple-800 mb-3">
        Paga {Number(planPrice).toFixed(2).replace(".", ",")} € y desbloquea al instante un cupón de 7 €
        para tu próximo pedido, además de envío gratis y puntos dobles.
      </p>

      <div className="grid grid-cols-2 gap-1.5 mb-4">
        {PERKS.map(p => (
          <div key={p} className="flex items-start gap-1.5 text-xs text-purple-700">
            <CheckCircle className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-0.5" />
            <span>{p}</span>
          </div>
        ))}
      </div>

      {gateways.length > 1 && (
        <div className="mb-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-500 mb-1.5">
            Método de pago
          </p>
          <div className="flex gap-2">
            {gateways.map(g => (
              <button
                key={g}
                type="button"
                data-testid={`prime-gateway-${g}`}
                onClick={() => setGateway(g)}
                className={`flex-1 rounded-xl border-2 px-3 py-2 text-xs font-bold transition ${
                  gateway === g
                    ? "border-purple-600 bg-purple-600 text-white"
                    : "border-purple-200 bg-white text-purple-700"
                }`}
              >
                {GATEWAY_LABEL[g] || g}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold gap-2 h-11 text-sm shadow-md disabled:opacity-60"
        disabled={!canPay || loading}
        data-testid="prime-activate-btn"
        onClick={startCheckout}
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" />Redirigiendo al pago…</>
          : <><Zap className="w-4 h-4" />PAGAR {Number(planPrice).toFixed(2).replace(".", ",")} € Y ACTIVAR</>}
      </Button>

      <p className="text-center text-[10px] text-purple-400 mt-1.5">
        {canPay
          ? "PRIME y el cupón se activan solo después de confirmarse el pago."
          : "Este restaurante todavía no tiene una pasarela de pago disponible para PRIME."}
      </p>
    </div>
  );
}
